import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getVisibleNavEntries } from '../../shared/ui/layout/navigationConfig';
import {
  buildNavigationAidsScope,
  buildRecentPageRecord,
  getBreadcrumbItems,
  getNavigationCommandItems,
  isTrackablePath,
  MAX_RECENT_PAGES,
  NAVIGATION_FAVORITES_STORAGE_KEY,
  NAVIGATION_RECENTS_STORAGE_KEY,
  normalizeFavoriteKeys,
  normalizeRecentPages,
} from '../../shared/ui/layout/navigationAids';
import { useAuthSession } from './AuthSessionProvider';
import { ENABLE_AUTH } from '../../shared/utils/constants';
import { canAccessPath } from '../router/accessControl';

const NavigationAidsContext = createContext({
  breadcrumbs: [],
  favoriteEntries: [],
  isActiveFavorite: false,
  recentPages: [],
  commandItems: [],
  commandPaletteOpen: false,
  toggleFavorite: () => {},
  openCommandPalette: () => {},
  closeCommandPalette: () => {},
});

function readScopedStoredJson(key, scope, normalizer) {
  if (typeof window === 'undefined') {
    return normalizer([]);
  }

  try {
    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) {
      return normalizer([]);
    }

    const parsed = JSON.parse(rawValue);
    if (Array.isArray(parsed)) {
      return normalizer(parsed);
    }

    if (parsed && typeof parsed === 'object') {
      return normalizer(parsed[scope] || []);
    }

    return normalizer([]);
  } catch (_error) {
    return normalizer([]);
  }
}

function writeScopedStoredJson(key, scope, value) {
  if (typeof window === 'undefined') {
    return;
  }

  let scopedStore = {};
  try {
    const rawValue = window.localStorage.getItem(key);
    const parsed = rawValue ? JSON.parse(rawValue) : {};
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      scopedStore = parsed;
    }
  } catch (_error) {
    scopedStore = {};
  }

  scopedStore[scope] = value;
  window.localStorage.setItem(key, JSON.stringify(scopedStore));
}

export function useNavigationAids() {
  return useContext(NavigationAidsContext);
}

export default function NavigationAidsProvider({ children }) {
  const location = useLocation();
  const { session, isReady: isAuthReady = true } = useAuthSession();
  const navigationScope = useMemo(
    () => buildNavigationAidsScope(session),
    [session]
  );
  const hasPathAccess = useCallback(
    (path) =>
      canAccessPath(path, session?.role, {
        enableAuth: ENABLE_AUTH,
        permissions: session?.permissions,
        authResolved: isAuthReady,
      }),
    [isAuthReady, session?.permissions, session?.role]
  );
  const availableNavEntries = useMemo(
    () =>
      getVisibleNavEntries(session?.role, {
        enableAuth: ENABLE_AUTH,
        permissions: session?.permissions,
        authResolved: isAuthReady,
      }).filter((entry) =>
        hasPathAccess(entry.key)
      ),
    [hasPathAccess, isAuthReady, session?.permissions, session?.role]
  );
  const [favoriteKeys, setFavoriteKeys] = useState(() =>
    readScopedStoredJson(NAVIGATION_FAVORITES_STORAGE_KEY, navigationScope, (value) =>
      normalizeFavoriteKeys(value, availableNavEntries)
    )
  );
  const [recentPages, setRecentPages] = useState(() =>
    readScopedStoredJson(NAVIGATION_RECENTS_STORAGE_KEY, navigationScope, (value) =>
      normalizeRecentPages(value, availableNavEntries, { hasPathAccess })
    )
  );
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const previousScopeRef = useRef(navigationScope);

  const currentNavEntry = useMemo(
    () =>
      availableNavEntries.find(
        (entry) =>
          location.pathname === entry.key || location.pathname.startsWith(`${entry.key}/`)
      ) || null,
    [availableNavEntries, location.pathname]
  );
  const breadcrumbs = useMemo(
    () => getBreadcrumbItems(location.pathname, availableNavEntries, { hasPathAccess }),
    [availableNavEntries, hasPathAccess, location.pathname]
  );
  const favoriteEntries = useMemo(
    () =>
      favoriteKeys
        .map((key) => availableNavEntries.find((entry) => entry.key === key))
        .filter(Boolean),
    [availableNavEntries, favoriteKeys]
  );
  const isActiveFavorite = useMemo(
    () => Boolean(currentNavEntry && favoriteKeys.includes(currentNavEntry.key)),
    [currentNavEntry, favoriteKeys]
  );
  const commandItems = useMemo(
    () =>
      getNavigationCommandItems({
        favoriteEntries,
        recentPages,
        pathname: location.pathname,
        availableEntries: availableNavEntries,
      }).filter((item) =>
        hasPathAccess(item.path)
      ),
    [availableNavEntries, favoriteEntries, hasPathAccess, location.pathname, recentPages]
  );

  const toggleFavorite = useCallback(
    (nextKey = currentNavEntry?.key) => {
      if (!nextKey) {
        return;
      }

      setFavoriteKeys((currentValue) => {
        if (currentValue.includes(nextKey)) {
          return currentValue.filter((item) => item !== nextKey);
        }

        return [nextKey, ...currentValue.filter((item) => item !== nextKey)];
      });
    },
    [currentNavEntry]
  );

  const openCommandPalette = useCallback(() => {
    setCommandPaletteOpen(true);
  }, []);

  const closeCommandPalette = useCallback(() => {
    setCommandPaletteOpen(false);
  }, []);

  useEffect(() => {
    if (!isTrackablePath(location.pathname)) {
      return;
    }

    const nextRecord = buildRecentPageRecord(location.pathname, availableNavEntries, {
      hasPathAccess,
    });
    if (!nextRecord) {
      return;
    }

    setRecentPages((currentValue) =>
      [
        nextRecord,
        ...currentValue.filter((item) => item.path !== nextRecord.path),
      ].slice(0, MAX_RECENT_PAGES)
    );
  }, [availableNavEntries, hasPathAccess, location.pathname]);

  useEffect(() => {
    setFavoriteKeys((currentValue) => normalizeFavoriteKeys(currentValue, availableNavEntries));
    setRecentPages((currentValue) =>
      normalizeRecentPages(currentValue, availableNavEntries, { hasPathAccess })
    );
  }, [availableNavEntries, hasPathAccess]);

  useEffect(() => {
    if (previousScopeRef.current === navigationScope) {
      return;
    }

    previousScopeRef.current = navigationScope;
    const scopedFavorites = readScopedStoredJson(
      NAVIGATION_FAVORITES_STORAGE_KEY,
      navigationScope,
      (value) => normalizeFavoriteKeys(value, availableNavEntries)
    );
    const scopedRecentPages = readScopedStoredJson(
      NAVIGATION_RECENTS_STORAGE_KEY,
      navigationScope,
      (value) => normalizeRecentPages(value, availableNavEntries, { hasPathAccess })
    );
    const currentRecentRecord = isTrackablePath(location.pathname)
      ? buildRecentPageRecord(location.pathname, availableNavEntries, { hasPathAccess })
      : null;

    setFavoriteKeys(
      scopedFavorites
    );
    setRecentPages(
      currentRecentRecord
        ? [
          currentRecentRecord,
          ...scopedRecentPages.filter((item) => item.path !== currentRecentRecord.path),
        ].slice(0, MAX_RECENT_PAGES)
        : scopedRecentPages
    );
  }, [availableNavEntries, hasPathAccess, location.pathname, navigationScope]);

  useEffect(() => {
    setCommandPaletteOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandPaletteOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    writeScopedStoredJson(NAVIGATION_FAVORITES_STORAGE_KEY, navigationScope, favoriteKeys);
  }, [favoriteKeys, navigationScope]);

  useEffect(() => {
    writeScopedStoredJson(NAVIGATION_RECENTS_STORAGE_KEY, navigationScope, recentPages);
  }, [navigationScope, recentPages]);

  const contextValue = useMemo(
    () => ({
      breadcrumbs,
      favoriteEntries,
      isActiveFavorite,
      recentPages,
      commandItems,
      commandPaletteOpen,
      toggleFavorite,
      openCommandPalette,
      closeCommandPalette,
    }),
    [
      breadcrumbs,
      closeCommandPalette,
      commandItems,
      commandPaletteOpen,
      favoriteEntries,
      isActiveFavorite,
      openCommandPalette,
      recentPages,
      toggleFavorite,
    ]
  );

  return (
    <NavigationAidsContext.Provider value={contextValue}>
      {children}
    </NavigationAidsContext.Provider>
  );
}
